<template>
  <div>
    <!-- avoid caller style to be conflicted with list's negative margin -->
    <ul :class="$style.options" v-if="!preview">
      <li :class="$style.options__item" v-for="item in options" :key="item.id">
        <label :class="$style.option">
          <DolphinCheckbox :value="item.id" v-model="_value" />
          <span :class="$style.option__text">{{ item.name }}</span>
        </label>
      </li>
    </ul>
    <template v-else>
      <ul :class="$style.list" v-if="modelValue.length > 0">
        <li
          :class="$style.list__item"
          :key="item.id"
          v-for="item in modelValue"
        >
          <OutlineChip :text="item.name" />
        </li>
      </ul>
      <slot v-else name="empty" />
    </template>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { defineComponent } from 'vue'
import type { Specialty } from '@/types/app'
import OutlineChip from '@/components/atoms/OutlineChip.vue'
import type { Normalized } from '@/libs/utilities/normalize'
import { normalize, denormalize } from '@/libs/utilities/normalize'
import DolphinCheckbox from '@/components/atoms/DolphinCheckbox.vue'

export default defineComponent({
  name: 'PlannerSpecialty',
  components: {
    OutlineChip,
    DolphinCheckbox,
  },
  props: {
    preview: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: Array as PropType<ReadonlyArray<Specialty>>,
      default: () => [],
    },
    options: {
      type: Array as PropType<ReadonlyArray<Specialty>>,
      default: () => [],
    },
  },
  computed: {
    optionsMap(): Normalized<Specialty, 'id'>['entities'] {
      const { entities } = normalize<Specialty, 'id'>(this.options, 'id')
      return entities
    },
    _value: {
      get(): Specialty['id'][] {
        return this.modelValue.map(specialty => specialty.id)
      },
      set(values: Normalized<Specialty, 'id'>['values']) {
        this.$emit(
          'update:modelValue',
          denormalize<Specialty, 'id'>(values, this.optionsMap)
        )
      },
    },
  },
})
</script>

<style lang="scss" module>
$gap: 8px;

.list {
  display: flex;
  flex-wrap: wrap;
  margin-top: -$gap;
  margin-left: -$gap;

  &__item {
    margin-top: $gap;
    margin-left: $gap;
    color: $color-text-and-object-accent-primary;
    font-size: 10px;
  }
}

.options {
  $gutter-horizontal: 40px;
  $gutter-vertical: 24px;

  display: flex;
  flex-wrap: wrap;
  margin-top: -$gutter-vertical;
  margin-left: -$gutter-horizontal;

  &__item {
    margin-top: $gutter-vertical;
    margin-left: $gutter-horizontal;
  }
}

.option {
  display: inline-flex;
  align-items: center;

  &__text {
    font-size: 14px;

    &:nth-child(n + 2) {
      margin-left: 4px;
    }
  }
}
</style>
